import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Phone, Upload, ShieldOff, PlayCircle, Trash2, PlusCircle } from 'lucide-react';

const LEAD_TYPES = [
  { id: 'expired', label: 'Expired' },
  { id: 'fsbo', label: 'FSBO' },
  { id: 'circle_prospect', label: 'Circle Prospect' },
  { id: 'open_house_followup', label: 'Open-house Follow-up' },
  { id: 'website_inquiry', label: 'Website Inquiry' },
  { id: 'buyer_lead', label: 'Buyer Lead' },
  { id: 'seller_lead', label: 'Seller Lead' },
  { id: 'past_client', label: 'Past Client' },
  { id: 'sphere', label: 'Sphere of Influence' },
  { id: 'investor', label: 'Investor' },
  { id: 'absentee_owner', label: 'Absentee Owner' },
  { id: 'probate', label: 'Probate' },
  { id: 'referral', label: 'Referral' },
  { id: 'custom', label: 'Custom' },
];

const ComplianceBadge = ({ level }) => {
  const cls = level === 'red' ? 'hq-badge-red' : level === 'yellow' ? 'hq-badge-yellow' : 'hq-badge-green';
  const label = level === 'red' ? 'Do Not Call' : level === 'yellow' ? 'Review' : 'Cleared';
  return <span className={`hq-badge ${cls}`} data-testid={`compliance-${level}`}>{label}</span>;
};

// Very simple CSV parser (headers required: name, phone; optional: email, address, lead_type, source)
function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim());
    const row = {};
    headers.forEach((h, idx) => { row[h] = cols[idx] || ''; });
    if (row.name && row.phone) {
      rows.push({
        name: row.name,
        phone: row.phone,
        email: row.email || undefined,
        address: row.address || undefined,
        lead_type: row.lead_type || 'custom',
        source: row.source || 'csv_import',
      });
    }
  }
  return rows;
}

export default function CallLeads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | callable | dnc
  const [showManual, setShowManual] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', lead_type: 'expired', source: '' });
  const [csvText, setCsvText] = useState('');
  const [campaign, setCampaign] = useState('');
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await http.get('/call/leads'); setLeads(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await http.post('/call/leads', form);
      setForm({ name: '', phone: '', email: '', address: '', lead_type: 'expired', source: '' });
      setShowManual(false);
      toast.success('Lead added.');
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const importCsv = async (e) => {
    e.preventDefault();
    const rows = parseCsv(csvText);
    if (!rows.length) return toast.error('No valid rows found. Header row required (name,phone,...)');
    try {
      const { data } = await http.post('/call/leads/import', { leads: rows, campaign });
      toast.success(`${data.imported} imported · ${data.duplicates} dupes · ${data.dnc_flagged} DNC-flagged · ${data.invalid} invalid`);
      setCsvText(''); setCampaign(''); setShowImport(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const suppress = async (id) => {
    if (!window.confirm('Add this number to your DNC list PERMANENTLY? Re-imports cannot bypass it.')) return;
    try {
      await http.post(`/call/leads/${id}/suppress`);
      toast.success('Suppressed. Number added to permanent DNC.');
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const startCall = async (lead) => {
    if (lead.do_not_call) return toast.error('This lead is on your DNC list.');
    try {
      const { data } = await http.post('/call/sessions', { lead_id: lead.id });
      nav(`/call/session/${data.session.id}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    try { await http.delete(`/call/leads/${id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const filtered = leads.filter(l => {
    if (filter === 'callable' && l.do_not_call) return false;
    if (filter === 'dnc' && !l.do_not_call) return false;
    if (q && !l.name.toLowerCase().includes(q.toLowerCase()) && !l.phone_display?.includes(q)) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <header className="hq-fade-in">
        <div className="flex items-center gap-3">
          <Link to="/call" className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>← Call Command</Link>
        </div>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="call-leads-title">Lead Lists</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Import your prospects from a CSV, add them by hand, or move them from other lists. Duplicates
          are auto-detected. Numbers on your DNC list are flagged in red — you cannot dial them.
        </p>
      </header>

      {/* Controls */}
      <div className="flex flex-wrap gap-3">
        <input
          className="hq-input flex-1 min-w-[220px]"
          placeholder="Search by name or phone…"
          value={q}
          onChange={e => setQ(e.target.value)}
          data-testid="lead-search"
        />
        <select className="hq-input max-w-[180px]" value={filter} onChange={e => setFilter(e.target.value)} data-testid="lead-filter">
          <option value="all">All leads</option>
          <option value="callable">Callable only</option>
          <option value="dnc">DNC only</option>
        </select>
        <button className="hq-btn-ghost inline-flex items-center gap-2" onClick={() => setShowImport(!showImport)} data-testid="toggle-import-btn">
          <Upload size={13} /> Import CSV
        </button>
        <button className="hq-btn-plum inline-flex items-center gap-2" onClick={() => setShowManual(!showManual)} data-testid="toggle-manual-btn">
          <PlusCircle size={13} /> New Lead
        </button>
      </div>

      {/* CSV Import */}
      {showImport && (
        <form onSubmit={importCsv} className="hq-card p-6 space-y-4 hq-fade-in">
          <div>
            <label className="hq-label">Campaign (optional)</label>
            <input className="hq-input" value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="e.g. Beacon Hill Expireds — Aug" />
          </div>
          <div>
            <label className="hq-label">CSV — headers: name,phone,email,address,lead_type,source</label>
            <textarea
              className="hq-input font-mono text-xs"
              rows={8}
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder={"name,phone,email,address,lead_type,source\nAinsley Grant,617-555-2033,ainsley@example.com,12 Beacon St,expired,mls"}
              data-testid="csv-textarea"
            />
          </div>
          <div className="text-right">
            <button className="hq-btn-primary" data-testid="import-csv-btn">Import</button>
          </div>
        </form>
      )}

      {/* Manual add */}
      {showManual && (
        <form onSubmit={createLead} className="hq-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4 hq-fade-in">
          <div><label className="hq-label">Name</label><input className="hq-input" required data-testid="new-lead-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div><label className="hq-label">Phone</label><input className="hq-input" required data-testid="new-lead-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="617-555-2033" /></div>
          <div><label className="hq-label">Email</label><input className="hq-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div><label className="hq-label">Property / Address</label><input className="hq-input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
          <div><label className="hq-label">Lead Type</label>
            <select className="hq-input" value={form.lead_type} onChange={e => setForm({...form, lead_type: e.target.value})}>
              {LEAD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div><label className="hq-label">Source</label><input className="hq-input" value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="MLS, referral, open house…" /></div>
          <div className="md:col-span-2 text-right">
            <button className="hq-btn-plum" data-testid="save-lead-btn">Save Lead</button>
          </div>
        </form>
      )}

      {/* Leads table */}
      <div className="hq-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead style={{ background: 'var(--parchment)', borderBottom: '1px solid var(--line)' }}>
              <tr>
                {['Name', 'Phone', 'Type', 'Source', 'Compliance', 'Calls', ''].map(h => (
                  <th key={h} className="text-left px-5 py-3 hq-eyebrow">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="leads-tbody">
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-16 text-center" style={{ color: 'var(--muted)' }}>
                  <div className="font-serif text-xl" style={{ color: 'var(--sage)' }}>No leads yet.</div>
                  <div className="text-sm mt-1">Import a CSV or add one manually.</div>
                </td></tr>
              )}
              {filtered.map(l => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--line-soft)' }} data-testid={`lead-row-${l.id}`}>
                  <td className="px-5 py-3 font-serif text-lg" style={{ color: 'var(--sage)' }}>{l.name}</td>
                  <td className="px-5 py-3 text-sm" style={{ color: 'var(--ink-soft)' }}>{l.phone_display || l.phone}</td>
                  <td className="px-5 py-3 text-xs uppercase tracking-widest" style={{ color: 'var(--muted)' }}>{l.lead_type}</td>
                  <td className="px-5 py-3 text-xs" style={{ color: 'var(--muted)' }}>{l.source || '—'}</td>
                  <td className="px-5 py-3"><ComplianceBadge level={l.compliance} /></td>
                  <td className="px-5 py-3 text-sm" style={{ color: 'var(--ink-soft)' }}>{l.call_count || 0}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {!l.do_not_call && (
                        <button
                          className="hq-btn-plum"
                          onClick={() => startCall(l)}
                          data-testid={`call-lead-${l.id}`}
                        >
                          <Phone size={11} className="inline mr-1" /> Call
                        </button>
                      )}
                      {!l.do_not_call && (
                        <button onClick={() => suppress(l.id)} title="Suppress permanently" data-testid={`suppress-${l.id}`}>
                          <ShieldOff size={15} style={{ color: 'var(--muted)' }} />
                        </button>
                      )}
                      <button onClick={() => remove(l.id)} title="Delete lead"><Trash2 size={15} style={{ color: 'var(--muted)' }} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
